import React, { useRef } from 'react';
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Typography,
} from '@material-ui/core';
import Button from '@material-ui/core/Button';
import { makeStyles } from '@material-ui/core/styles';
import { HighlightOffRounded, InsertDriveFileOutlined, OpenInNew, Print } from '@material-ui/icons';
import { useReactToPrint } from 'react-to-print';
import {
  DocumentTemplateType,
  FileCategoryType,
  INumberedDocumentView,
  ManufacturersWarrantyType,
} from '../../api-client/autogenerated';
import PDFIcon from '../icons/PDF-icon';
import { fileIsPdf, getPageTitle, openInNewTab } from '../../scripts/utils';

const useStyles = makeStyles(() => ({
  titleContainer: {
    display: 'flex',
    flexDirection: 'column',
    padding: '8px 16px 8px 16px',
    borderBottom: '1px solid #EDECEC',
  },
  title: {
    marginTop: '3px',
    marginLeft: '6px',
    fontFamily: 'Roboto',
    fontStyle: 'normal',
    fontWeight: 500,
    fontSize: '26px',
    lineHeight: '30px',
    textAlign: 'left',
    color: '#0947B9',
  },
  rootIconButton: {
    padding: 0,
    '&:hover': {
      backgroundColor: 'transparent',
    },
  },
  row: {
    display: 'flex',
    flexWrap: 'wrap',
    padding: '12px 6px',
    '@media print': {
      // Keep a label and its answer together rather than splitting them across pages.
      pageBreakInside: 'avoid',
    },
  },
  /** Identifies the printout; on screen the dialog's own title already says this. */
  printHeader: {
    display: 'none',
    '@media print': {
      display: 'block',
      marginBottom: 12,
      fontFamily: 'Roboto',
      fontStyle: 'normal',
      fontWeight: 500,
      fontSize: '18px',
      lineHeight: '22px',
      color: '#000',
    },
  },
  noPrint: {
    '@media print': {
      display: 'none',
    },
  },
  label: {
    width: '40%',
    minWidth: 200,
    paddingRight: 16,
    fontFamily: 'Roboto',
    fontStyle: 'normal',
    fontWeight: 500,
    fontSize: '12px',
    lineHeight: '18px',
    textTransform: 'uppercase',
    color: '#616061',
  },
  value: {
    flex: 1,
    minWidth: 220,
    fontFamily: 'Roboto',
    fontStyle: 'normal',
    fontWeight: 400,
    fontSize: '15px',
    lineHeight: '20px',
    color: '#464546',
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
  },
  file: {
    display: 'inline-flex',
    alignItems: 'center',
  },
  fileName: {
    paddingLeft: '8px',
    fontFamily: 'Roboto',
    fontStyle: 'normal',
    fontWeight: 400,
    fontSize: '13px',
    lineHeight: '29px',
    color: '#464546',
    wordBreak: 'break-all',
  },
}));

interface Props {
  open: boolean;
  handleClose: () => void;
  document: INumberedDocumentView;
}

const warrantyLabels: { [key in ManufacturersWarrantyType]: string } = {
  [ManufacturersWarrantyType.Same]: 'Same',
  [ManufacturersWarrantyType.Different]: 'Different',
};

/**
 * Read-only view of everything the contractor filled in on the bidding portal's
 * "Add Substitution Request" dialog, so a consultant reviewing the request can see it
 * without leaving their review.
 */
export default function SubstitutionRequestInfoDialog(props: Props) {
  const classes = useStyles();
  const { open, handleClose, document } = props;

  const printRef = useRef<HTMLDivElement>(null);
  const printTitle = `${getPageTitle(
    DocumentTemplateType.SubstitutionRequests,
    document,
  )} — Contractor Provided Info`;
  /**
   * The same rendered rows in a window of their own, so the form can sit beside the review
   * instead of on top of it. React has already escaped everything in the node being cloned, and
   * the app's stylesheets come along so it looks the same; the file's View button does not
   * travel, because its click handler lives in this app.
   */
  const handleOpenInNewWindow = () => {
    const content = printRef.current;
    if (!content) return;
    const popup = window.open('', '_blank', 'width=760,height=900,resizable=yes,scrollbars=yes');
    if (!popup) return;
    const styles = Array.from(window.document.querySelectorAll('style, link[rel="stylesheet"]'))
      .map((node) => node.outerHTML)
      .join('');
    const clone = content.cloneNode(true) as HTMLElement;
    clone.querySelectorAll('button').forEach((button) => button.remove());
    const escapedTitle = printTitle.replace(/&/g, '&amp;').replace(/</g, '&lt;');
    popup.document.write(
      `<!DOCTYPE html><html><head><title>${escapedTitle}</title>${styles}</head>` +
        `<body style="margin:0;padding:16px;background:#FFF">` +
        `<h2 style="font-family:Roboto,sans-serif;font-weight:500;font-size:22px;color:#0947B9;margin:0 0 12px 6px">${escapedTitle}</h2>` +
        `${clone.innerHTML}</body></html>`,
    );
    popup.document.close();
  };

  const handlePrint = useReactToPrint({
    content: () => printRef.current,
    documentTitle: printTitle,
    // react-to-print's default page style is a 0mm margin, which printers clip. This is a record
    // people file, so give it a real margin; the colour-adjust rule is carried over from that
    // default rather than dropped.
    pageStyle:
      '@page { size: auto; margin: 12mm; } @media print { body { -webkit-print-color-adjust: exact; } }',
  });

  const attachments =
    document.files?.filter((file) => file.category === FileCategoryType.DocumentAttachments) || [];

  const fields: { label: string; value?: string | number | null }[] = [
    { label: 'Spec Section', value: document.submittalSection },
    { label: 'Page Number', value: document.pageNumber },
    { label: 'Paragraph Number', value: document.paragraphNumber },
    { label: 'Description of substitution', value: document.description },
    { label: 'Proposed Substitution', value: document.proposedSubstitution },
    { label: 'Reason for Substitution', value: document.substitutionReason },
    {
      label: 'List ways in which the proposed substitution affects dimensions shown on drawings',
      value: document.substitutionAffectsDrawing,
    },
    {
      label: 'List effects of proposed substitution on other trades',
      value: document.substitutionAffectsTrades,
    },
    {
      label:
        'List ways in which substitution would be affected by applicable code requirements and agency approval',
      value: document.agency,
    },
    {
      label: 'List differences between proposed substitution and specified item',
      value: document.substitutionDifferences,
    },
    {
      label: 'Manufacturers warranties',
      value: document.manufacturersWarranties
        ? warrantyLabels[document.manufacturersWarranties]
        : undefined,
    },
    { label: 'Explain', value: document.manufacturersWarrantiesExplanation },
    {
      label:
        'List information on availability of maintenance service and source of replacement materials',
      value: document.informationAvailable,
    },
  ];

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle className={classes.titleContainer}>
        <Typography className={classes.title} align="left">
          Contractor Provided Info
        </Typography>
        <IconButton
          style={{ right: '20px', top: '12px', position: 'absolute' }}
          onClick={handleClose}
          classes={{
            root: classes.rootIconButton,
          }}
        >
          <HighlightOffRounded />
        </IconButton>
      </DialogTitle>
      <DialogContent style={{ paddingTop: 8, paddingBottom: 8 }}>
        <div ref={printRef}>
          <Typography className={classes.printHeader}>{printTitle}</Typography>
          {fields.map(({ label, value }, index) => (
            <React.Fragment key={label}>
              {index > 0 ? <Divider /> : null}
              <div className={classes.row}>
                <Typography className={classes.label}>{label}</Typography>
                <Typography className={classes.value}>
                  {value === undefined || value === null || value === '' ? '—' : value}
                </Typography>
              </div>
            </React.Fragment>
          ))}
          <Divider />
          <div className={classes.row}>
            <Typography className={classes.label}>Additional File</Typography>
            <div className={classes.value}>
              {attachments.length === 0
                ? '—'
                : attachments.map((file) => (
                    <div key={file.id} className={classes.file}>
                      {fileIsPdf(file) ? (
                        <PDFIcon />
                      ) : (
                        <InsertDriveFileOutlined htmlColor="#7A797A" style={{ fontSize: 30 }} />
                      )}
                      <Typography className={classes.fileName}>{file.name}</Typography>
                      <Button
                        color="primary"
                        className={classes.noPrint}
                        disabled={!file.isVerified || !fileIsPdf(file)}
                        onClick={() => openInNewTab(file.id)}
                        style={{ height: '24px', marginLeft: 8 }}
                      >
                        View
                      </Button>
                    </div>
                  ))}
            </div>
          </div>
        </div>
      </DialogContent>
      <DialogActions style={{ padding: '8px 24px 16px' }}>
        <Button
          variant="outlined"
          color="primary"
          startIcon={<OpenInNew />}
          onClick={handleOpenInNewWindow}
          style={{ height: 32 }}
        >
          Open in New Window
        </Button>
        <Button
          variant="outlined"
          color="primary"
          startIcon={<Print />}
          onClick={handlePrint}
          style={{ height: 32 }}
        >
          Print
        </Button>
      </DialogActions>
    </Dialog>
  );
}
